/* eslint-disable react-hooks/exhaustive-deps */
/**
 * XTerminal Component
 *
 * Reusable terminal emulator component using xterm.js
 * Supports both buffered execution and SSE streaming
 */

import React, { useEffect, useRef, useState } from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'
import { TerminalAPI } from '../../lib/api-client'

interface XTerminalProps {
  sessionId?: string
  mode?: 'local' | 'sandbox' | 'bastion'
  onSessionCreated?: (sessionId: string) => void
  onError?: (error: string) => void
  theme?: {
    background?: string
    foreground?: string
    cursor?: string
  }
}

export const XTerminal: React.FC<XTerminalProps> = ({
  sessionId: externalSessionId,
  mode = 'local',
  onSessionCreated,
  onError,
  theme = {
    background: '#1a1b26',
    foreground: '#c0caf5',
    cursor: '#c0caf5',
  },
}) => {
  const terminalRef = useRef<HTMLDivElement>(null)
  const xtermRef = useRef<Terminal | null>(null)
  const fitAddonRef = useRef<FitAddon | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(externalSessionId || null)
  const [isReady, setIsReady] = useState(false)
  const commandBufferRef = useRef<string>('')
  const commandHistoryRef = useRef<string[]>([])
  const historyIndexRef = useRef<number>(-1)

  // Initialize xterm.js
  useEffect(() => {
    if (!terminalRef.current) return

    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme,
      allowTransparency: true,
      scrollback: 10000,
    })

    const fitAddon = new FitAddon()
    terminal.loadAddon(fitAddon)

    terminal.open(terminalRef.current)
    fitAddon.fit()

    xtermRef.current = terminal
    fitAddonRef.current = fitAddon

    // Welcome message
    terminal.writeln('\x1b[1;32m╔══════════════════════════════════════════╗\x1b[0m')
    terminal.writeln('\x1b[1;32m║\x1b[0m  \x1b[1;36mLUKHAS Terminal\x1b[0m - Λ Consciousness    \x1b[1;32m║\x1b[0m')
    terminal.writeln('\x1b[1;32m╚══════════════════════════════════════════╝\x1b[0m')
    terminal.writeln('')
    terminal.writeln(`Mode: \x1b[1;33m${mode}\x1b[0m`)
    terminal.writeln('Initializing session...')
    terminal.writeln('')

    // Handle resize
    const handleResize = () => {
      fitAddon.fit()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      terminal.dispose()
    }
  }, [mode, theme])

  // Create session
  useEffect(() => {
    if (!xtermRef.current || sessionId) return

    const createSession = async () => {
      try {
        const session = await TerminalAPI.createSession(mode)
        setSessionId(session.session_id)
        onSessionCreated?.(session.session_id)

        xtermRef.current?.writeln(`\x1b[1;32m✓\x1b[0m Session created: ${session.session_id}`)
        xtermRef.current?.writeln('')
        xtermRef.current?.write('\x1b[1;36m$\x1b[0m ')
        setIsReady(true)
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        xtermRef.current?.writeln(`\x1b[1;31m✗\x1b[0m Failed to create session: ${errorMsg}`)
        onError?.(errorMsg)
      }
    }

    createSession()
  }, [sessionId, mode, onSessionCreated, onError])

  // Handle user input
  useEffect(() => {
    if (!xtermRef.current || !isReady || !sessionId) return

    const terminal = xtermRef.current

    const handleData = (data: string) => {
      const code = data.charCodeAt(0)

      // Enter key
      if (code === 13) {
        terminal.writeln('')
        const command = commandBufferRef.current.trim()

        if (command) {
          executeCommand(command)
          commandHistoryRef.current.push(command)
          historyIndexRef.current = commandHistoryRef.current.length
        } else {
          terminal.write('\x1b[1;36m$\x1b[0m ')
        }

        commandBufferRef.current = ''
        return
      }

      // Backspace
      if (code === 127) {
        if (commandBufferRef.current.length > 0) {
          commandBufferRef.current = commandBufferRef.current.slice(0, -1)
          terminal.write('\b \b')
        }
        return
      }

      // Arrow Up - Previous command
      if (data === '\x1b[A') {
        if (historyIndexRef.current > 0) {
          historyIndexRef.current--
          const prevCommand = commandHistoryRef.current[historyIndexRef.current]

          // Clear current line
          terminal.write('\r\x1b[K')
          terminal.write('\x1b[1;36m$\x1b[0m ')
          terminal.write(prevCommand)
          commandBufferRef.current = prevCommand
        }
        return
      }

      // Arrow Down - Next command
      if (data === '\x1b[B') {
        if (historyIndexRef.current < commandHistoryRef.current.length - 1) {
          historyIndexRef.current++
          const nextCommand = commandHistoryRef.current[historyIndexRef.current]

          // Clear current line
          terminal.write('\r\x1b[K')
          terminal.write('\x1b[1;36m$\x1b[0m ')
          terminal.write(nextCommand)
          commandBufferRef.current = nextCommand
        } else if (historyIndexRef.current === commandHistoryRef.current.length - 1) {
          historyIndexRef.current++
          terminal.write('\r\x1b[K')
          terminal.write('\x1b[1;36m$\x1b[0m ')
          commandBufferRef.current = ''
        }
        return
      }

      // Ctrl+C - Cancel current command
      if (code === 3) {
        terminal.writeln('^C')
        terminal.write('\x1b[1;36m$\x1b[0m ')
        commandBufferRef.current = ''
        return
      }

      // Ctrl+L - Clear screen
      if (code === 12) {
        terminal.clear()
        terminal.write('\x1b[1;36m$\x1b[0m ')
        return
      }

      // Regular character
      if (code >= 32 && code < 127) {
        commandBufferRef.current += data
        terminal.write(data)
      }
    }

    const disposable = terminal.onData(handleData)

    return () => {
      disposable.dispose()
    }
  }, [isReady, sessionId])

  // Execute command
  const executeCommand = async (command: string) => {
    const terminal = xtermRef.current
    if (!terminal || !sessionId) return

    try {
      // Check for streaming commands (long-running)
      const streamingCommands = ['tail', 'watch', 'top', 'htop', 'docker logs', 'kubectl logs']
      const shouldStream = streamingCommands.some(cmd => command.startsWith(cmd))

      if (shouldStream) {
        // Use SSE streaming
        const eventSource = TerminalAPI.createStream(sessionId, command)

        eventSource.onmessage = (event) => {
          const data = JSON.parse(event.data)

          if (data.type === 'stdout' && data.data) {
            terminal.write(data.data)
          } else if (data.type === 'stderr' && data.data) {
            terminal.write(`\x1b[1;31m${data.data}\x1b[0m`)
          } else if (data.type === 'exit') {
            terminal.writeln('')
            if (data.code !== 0) {
              terminal.writeln(`\x1b[1;31mExit code: ${data.code}\x1b[0m`)
            }
            terminal.write('\x1b[1;36m$\x1b[0m ')
            eventSource.close()
          }
        }

        eventSource.onerror = (error) => {
          terminal.writeln(`\x1b[1;31mStream error: ${error}\x1b[0m`)
          terminal.write('\x1b[1;36m$\x1b[0m ')
          eventSource.close()
        }

      } else {
        // Use buffered execution
        const result = await TerminalAPI.exec(sessionId, command)

        if (result.stdout) {
          terminal.write(result.stdout)
        }

        if (result.stderr) {
          terminal.write(`\x1b[1;31m${result.stderr}\x1b[0m`)
        }

        if (result.exit_code !== 0) {
          terminal.writeln(`\x1b[1;31mExit code: ${result.exit_code}\x1b[0m`)
        }

        terminal.write('\x1b[1;36m$\x1b[0m ')
      }

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Execution failed'
      terminal.writeln(`\x1b[1;31m✗\x1b[0m ${errorMsg}`)
      terminal.write('\x1b[1;36m$\x1b[0m ')
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionId) {
        TerminalAPI.closeSession(sessionId).catch(console.error)
      }
    }
  }, [sessionId])

  return (
    <div
      ref={terminalRef}
      className="w-full h-full"
      style={{ minHeight: '400px' }}
    />
  )
}
